import CustomAsyncLoaderPlugin from "../../../customPlugin/customAsyncLoader";
import { requestNextAdaptiveTrials, shouldUseMockBackend } from "../../../services/adaptiveTrialsService";
import { saveAdaptiveFlowError } from "../../../services/latencies/adaptiveFlowLogStore";
import { Game } from "../../../gearshiftGame/game";
import { prepareBoardPreview } from "./missionHelpers";

const ERROR_PAGE_URL = "somethingWentWrong.html";

/**
 * Reads the `start_date` jsPsych stamps on every trial's data (added once via
 * `jsPsych.data.addProperties` at experiment start).
 */
function getSessionStartDate(jsPsych) {
  const values = jsPsych.data.get().values();
  return values.length > 0 ? values[values.length - 1]?.start_date : undefined;
}

/**
 * Builds a jsPsych node that live-fetches and renders more trials from the
 * backend whenever the queue runs dry, until a trial with `validation.blockType
 * === 98` arrives - the backend's signal that this is the mission's last trial.
 * That trial is still rendered normally (e.g. it's typically the FEEDBACK trial
 * showing the final score), it just also ends the mission once it's done. The
 * mission-complete POST for that isn't sent from here - see jehliGearshiftTask.js's
 * `on_trial_finish` instead, which isn't skipped by a crash/low-score trial calling
 * `jsPsych.endExperiment()` the way this node's `on_timeline_finish` would be. Since
 * jsPsych builds its trial tree once up front, this works by pre-building one
 * reusable node per known trial type, each gated by a `conditional_function`
 * that only lets it run when it matches the currently queued trial's type -
 * which is also what lets a batch mix types (TASK/MESSAGE/SURVEY/FEEDBACK) freely.
 *
 * Append the returned `node` to the end of a mission's static timeline once its
 * `trials` config is exhausted, for missions with `missionIsAdaptive: true`.
 *
 * Each builder also gets a `prefetchNextBatch` function (5th arg) to call early
 * (e.g. TASK's phase 7 `on_load`) so the fetch overlaps that instead of blocking
 * on the loader trial - a no-op if the queue's still warm, safe to call from
 * anywhere. The loader below calls the same function as a fallback.
 *
 * @param {Object} options
 * @param {JsPsych} options.jsPsych jsPsych instance running the experiment
 * @param {Object} options.metadata The global metadata of the experiment
 * @param {string} options.participantID The current participant's ID
 * @param {number} options.missionNumber The mission these trials belong to
 * @param {{block: number, trialNumber: number}} options.initialCursor Block/trial numbering to resume from (typically the last static trial's values)
 * @param {Object} options.templateTrial The last static trial config, shown as an idle background during the loading indicator before anything adaptive has rendered yet
 * @param {Score} options.score The mission's current Score instance, used to position the loading indicator's idle board preview correctly
 * @param {Object.<string, (jsPsych: JsPsych, metadata: Object, trialConfig: (() => Object), stimulus: (() => string), prefetchNextBatch: (() => Promise<void>)) => Object>} options.trialBuilders
 *   Map of trialType -> mission-specific builder (e.g. `{ TASK: buildMissionZeroTask, MESSAGE: buildMissionZeroMessage, ... }`).
 *   Each builder must accept a trialConfig *getter* and a stimulus getter, same as the static trial builders already do; the
 *   trailing `prefetchNextBatch` arg can be safely ignored by builders that have nowhere sensible to call it from.
 * @param {(trialConfig: Object) => string} options.createStimulusFn Builds the stimulus HTML for a given trial config
 * @returns {{ node: Object, prefetchNextBatch: (() => Promise<void>) }}
 *   `node` is the jsPsych timeline node to append; `prefetchNextBatch` is the same function passed to `trialBuilders`,
 *   exposed so the *last static* trial (built before this node even exists) can also warm the queue.
 */
export function buildAdaptiveTrialsNode({
  jsPsych,
  metadata,
  participantID,
  missionNumber,
  initialCursor,
  templateTrial,
  score,
  trialBuilders,
  createStimulusFn,
}) {
  const state = {
    queue: [],
    cursor: { ...initialCursor },
    exhausted: false,
    errored: false,
    errorReason: null,
    pendingFetch: null,
  };
  const current = { trialConfig: null };

  // No-op if the queue's warm; dedupes concurrent callers onto one in-flight
  // request. Never rejects - errors are recorded on `state` instead, since
  // callers besides the loader trial have no error UI to hand them to.
  const fetchNextBatchIfNeeded = () => {
    if (state.exhausted || state.errored || state.queue.length > 0) return Promise.resolve();
    if (state.pendingFetch) return state.pendingFetch;

    state.pendingFetch = (async () => {
      try {
        const trials = await requestNextAdaptiveTrials({
          participantID,
          startDate: getSessionStartDate(jsPsych),
          missionNumber,
          block: state.cursor.block,
          trialNumber: state.cursor.trialNumber,
        });

        // An unrenderable trial type means the backend and this build are out of
        // sync - that's a hard stop, not something to silently skip past.
        const unsupported = trials.find((trial) => !trialBuilders[trial.trialType]);
        if (unsupported) {
          // TODO: no way yet to report `state.errorReason` back to the backend -
          // for now this just surfaces the connection-error screen.
          state.errorReason = `Unsupported trialType "${unsupported.trialType}" (trial ${unsupported.trialNumber}, block ${unsupported.block})`;
          const error = new Error(state.errorReason);
          // response-content problem, so log the offending trial instead.
          error.unsupportedTrial = unsupported;
          throw error;
        }

        state.queue.push(...trials);
        if (trials.length > 0) {
          const lastFetchedTrial = trials[trials.length - 1];
          state.cursor.block = lastFetchedTrial.block ?? state.cursor.block;
          state.cursor.trialNumber = lastFetchedTrial.trialNumber ?? state.cursor.trialNumber;
        }
        // blockType 98 = mission's last trial, still rendered normally, just stops further
        // fetches (mission-complete notification is sent from jehliGearshiftTask.js instead).
        if (trials.some((trial) => trial.validation?.blockType === 98)) state.exhausted = true;
      } catch (error) {
        console.error("[AdaptiveTrials] Failed to fetch the next trial batch:", error);
        state.errored = true;
        state.errorReason = state.errorReason || error?.message || "Unknown adaptive trials error";

        if (!shouldUseMockBackend()) {
          saveAdaptiveFlowError({
            timestamp: new Date().toISOString(),
            participant_id: participantID || "unknown",
            missionNumber,
            block: state.cursor.block,
            trialNumber: state.cursor.trialNumber,
            errorReason: state.errorReason,
            errorName: error?.name || null,
            baseUrl: error?.baseUrl || null,
            payload: error?.payload || null,
            unsupportedTrial: error?.unsupportedTrial || null,
          });
        }
      } finally {
        state.pendingFetch = null;
      }
    })();

    return state.pendingFetch;
  };

  const loaderTrial = {
    type: CustomAsyncLoaderPlugin,
    // Needed so jehliGearshiftTask.js keeps the cursor hidden during loading.
    data: { trialConfig: () => current.trialConfig || templateTrial },
    // Keep showing the last rendered trial's board (idle, no active phases)
    // behind the loading indicator, so the wait doesn't flash a blank screen.
    stimulus: () => createStimulusFn(current.trialConfig || templateTrial),
    // The raw board markup above positions elements from CSS defaults only,
    // which doesn't match where a running trial actually places them (driver
    // centered, dividers laid out, etc). Reuse the same static-preview setup
    // tutorials use behind their ready/congrats overlays to fix that up.
    afterRender: () => {
      const previewGame = new Game(jsPsych, metadata, score, current.trialConfig || templateTrial);
      prepareBoardPreview(previewGame, 0);
    },
    loadFn: async () => {
      // Usually a no-op - `prefetchNextBatch` warmed the queue already. Fallback
      // for whatever's still in flight, or the very first batch.
      await fetchNextBatchIfNeeded();

      if (state.errored) {
        current.trialConfig = null;
        const reasonParam = state.errorReason ? `?reason=${encodeURIComponent(state.errorReason)}` : "";
        window.location.href = `${ERROR_PAGE_URL}${reasonParam}`;
        return new Promise(() => { });
      }

      current.trialConfig = state.queue.shift() ?? null;
      return { name: "adaptiveTrialsLoader", adaptiveQueueRemaining: state.queue.length };
    },
  };

  // One reusable node per known trial type, each only running on the pass
  // where the currently-queued trial actually matches that type.
  const typeSlots = Object.keys(trialBuilders).map((trialType) => {
    const slotTrial = trialBuilders[trialType](
      jsPsych,
      metadata,
      () => current.trialConfig,
      () => createStimulusFn(current.trialConfig),
      fetchNextBatchIfNeeded
    );
    return {
      timeline: [slotTrial],
      conditional_function: () => !state.errored && current.trialConfig?.trialType === trialType,
    };
  });

  return {
    node: {
      timeline: [loaderTrial, ...typeSlots],
      // Deliberately ignores `state.errored`: an error can now surface async
      // (from `prefetchNextBatch` mid-trial) instead of only inside loadFn, so
      // this must always loop back to the loader to give it a chance to catch that
      loop_function: () => current.trialConfig !== null,
    },
    prefetchNextBatch: fetchNextBatchIfNeeded,
  };
}
